import { useState } from "react";
import { toast } from "sonner";
import { HandHeart, Users, Handshake } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import { IMG } from "../lib/content";
import { submitVolunteer, submitPartner, apiError } from "../lib/api";

const interests = ["Youth work", "Events & festivals", "Community outreach", "Fundraising", "Admin & operations", "Wherever I'm needed"];
const collabTypes = ["Community group", "Charity / third sector", "Education", "Business / corporate", "Public sector", "Local authority", "Other"];
const field = "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-base text-nead-navy placeholder:text-nead-navy/40 outline-none focus:border-nead-pink transition-colors";

function VolunteerForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "Wherever I'm needed", availability: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitVolunteer(form);
      toast.success("Thank you for signing up. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", interest: "Wherever I'm needed", availability: "", message: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="volunteer-form">
      <input required value={form.name} onChange={set("name")} placeholder="Your name" aria-label="Name" className={field} data-testid="vol-name" />
      <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" aria-label="Email" className={field} data-testid="vol-email" />
      <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" aria-label="Phone" className={field} data-testid="vol-phone" />
      <select value={form.interest} onChange={set("interest")} aria-label="Interest" className={`${field} cursor-pointer`} data-testid="vol-interest">
        {interests.map((t) => <option key={t}>{t}</option>)}
      </select>
      <input value={form.availability} onChange={set("availability")} placeholder="Availability (e.g. weekends)" aria-label="Availability" className={field} data-testid="vol-availability" />
      <textarea value={form.message} onChange={set("message")} placeholder="Anything else you'd like us to know?" aria-label="Message" rows={3} className={`${field} resize-none`} data-testid="vol-message" />
      <Button type="submit" disabled={busy} testId="volunteer-submit">{busy ? "Sending…" : "Sign me up"}</Button>
    </form>
  );
}

function CollaborateForm() {
  const [form, setForm] = useState({ organisation: "", name: "", email: "", partnership_type: "Community group", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitPartner(form);
      toast.success("Thank you. We'll be in touch to explore working together.");
      setForm({ organisation: "", name: "", email: "", partnership_type: "Community group", message: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="collab-form">
      <input required value={form.organisation} onChange={set("organisation")} placeholder="Organisation / group name" aria-label="Organisation" className={field} data-testid="collab-org" />
      <input required value={form.name} onChange={set("name")} placeholder="Your name" aria-label="Name" className={field} data-testid="collab-name" />
      <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" aria-label="Email" className={field} data-testid="collab-email" />
      <select value={form.partnership_type} onChange={set("partnership_type")} aria-label="Type" className={`${field} cursor-pointer`} data-testid="collab-type">
        {collabTypes.map((t) => <option key={t}>{t}</option>)}
      </select>
      <textarea required value={form.message} onChange={set("message")} placeholder="Tell us about your idea or project…" aria-label="Message" rows={4} className={`${field} resize-none`} data-testid="collab-message" />
      <Button type="submit" disabled={busy} testId="collab-submit">{busy ? "Sending…" : "Start a collaboration"}</Button>
    </form>
  );
}

const tabs = [
  { key: "volunteer", label: "Volunteer", icon: HandHeart },
  { key: "collaborate", label: "Collaborate", icon: Handshake },
];

export default function GetInvolved() {
  const [tab, setTab] = useState("volunteer");
  return (
    <div>
      <PageHeader label="Get Involved" titleLines={["Help build", "what's next."]}
        intro="There's a place for everyone at NEAD. Give your time as a volunteer, or bring your organisation together with us to create something new." />

      <section className="container-x pt-16 md:pt-20">
        <Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            <button onClick={() => setTab("volunteer")} data-testid="tab-volunteer" className={`text-left rounded-[1.5rem] border p-8 transition-all ${tab === "volunteer" ? "border-nead-pink bg-nead-mist" : "border-black/[0.07] hover:border-nead-pink/40"}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-full grad-pink-cyan"><HandHeart className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
              <h3 className="mt-5 font-display font-bold text-2xl text-nead-navy">Volunteer with us</h3>
              <p className="mt-2 text-nead-navy/60 leading-relaxed text-[0.95rem]">Give your time at events, youth sessions and community projects. No experience needed, just enthusiasm.</p>
            </button>
            <button onClick={() => setTab("collaborate")} data-testid="tab-collaborate" className={`text-left rounded-[1.5rem] border p-8 transition-all ${tab === "collaborate" ? "border-nead-pink bg-nead-mist" : "border-black/[0.07] hover:border-nead-pink/40"}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-full grad-pink-cyan"><Users className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
              <h3 className="mt-5 font-display font-bold text-2xl text-nead-navy">Start a collaboration</h3>
              <p className="mt-2 text-nead-navy/60 leading-relaxed text-[0.95rem]">Bring your group, charity, school or business together with NEAD to deliver a project or event.</p>
            </button>
          </div>
        </Reveal>
      </section>

      <section className="container-x py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="flex gap-2 mb-8">
              {tabs.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`switch-${t.key}`} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "bg-nead-navy text-white" : "bg-nead-mist text-nead-navy/70 hover:text-nead-pink"}`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </button>
              ))}
            </div>
            <Eyebrow className="text-nead-pink">{tab === "volunteer" ? "Sign up to volunteer" : "Let's work together"}</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy">{tab === "volunteer" ? "Ready when you are." : "Tell us your idea."}</h2>
            <div className="mt-10 overflow-hidden rounded-[1.5rem] aspect-[4/3] hidden lg:block">
              <img src={IMG.volunteersDonate} alt="Community activity" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            {tab === "volunteer" ? <VolunteerForm /> : <CollaborateForm />}
          </Reveal>
        </div>
      </section>

      <section className="bg-nead-ink text-white">
        <div className="container-x py-16 md:py-20 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight max-w-2xl mx-auto">Looking to sponsor or donate instead?</h2>
          <p className="mt-3 text-white/60">Support our work financially and help us reach more people.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button to="/partners" variant="pink" testId="gi-sponsor">Sponsor us</Button>
            <Button to="/donate" variant="outline-light" testId="gi-donate">Donate</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
